import type { ReactNode } from "react";
import useSWR from "swr";
import {
  BarChart3,
  Search,
  Gauge,
  ClipboardList,
  Loader2,
  AlertTriangle,
  type LucideIcon,
} from "lucide-react";
import { relativeTime } from "@/lib/utils";
import type { Tone } from "@/lib/colors";
import { Pill } from "@/components/ui";

type PhaseState = "never" | "running" | "stalled" | "synced";

interface PhaseStatus {
  state: PhaseState;
  done: number;
  total: number;
  syncedAt: string | null;
}

interface Status {
  // Жизненный цикл каждой фазы считается на сервере (services/phaseStatus.ts).
  // Пилюля чисто презентационная — «идёт / застряла / готово» решает сервер.
  phases?: Record<"stats" | "inspect" | "perf", PhaseStatus>;
  // Последний синк рег (max Network.lastSyncAt по включённым сетям).
  regsLastSyncAt?: string | null;
}

const PHASES: {
  key: "stats" | "inspect" | "perf";
  label: string;
  icon: LucideIcon;
}[] = [
  { key: "stats", label: "Стата", icon: BarChart3 },
  { key: "inspect", label: "Инспекция", icon: Search },
  { key: "perf", label: "PageSpeed", icon: Gauge },
];

// Тон + подсказка + тело по состоянию фазы. Живёт здесь (не на сервере),
// потому что это чистое представление; сервер решает только `state`.
function render(label: string, Icon: LucideIcon, p: PhaseStatus) {
  const progress = `${p.done}/${p.total}`;
  switch (p.state) {
    case "running":
      return {
        tone: "brand" as Tone,
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        body: progress,
        title: `${label}: синхронизация идёт (${progress})`,
      };
    case "stalled":
      return {
        tone: "warn" as Tone,
        icon: <AlertTriangle className="h-3 w-3" />,
        body: progress,
        title: `${label}: очередь застряла (${progress}) — задачи не обрабатываются`,
      };
    case "synced":
      return {
        tone: "ok" as Tone,
        icon: <Icon className="h-3 w-3" />,
        body: p.syncedAt ? relativeTime(p.syncedAt) : "✓",
        title: p.syncedAt
          ? `${label}: последняя синхронизация ${relativeTime(p.syncedAt)}`
          : `${label}: синхронизировано`,
      };
    default: // "never"
      return {
        tone: "muted" as Tone,
        icon: <Icon className="h-3 w-3" />,
        body: "—",
        title: `${label}: синхронизация ещё не выполнялась`,
      };
  }
}

// Чип шапки. В покое — иконка + значение; при наведении плавно раскрывается,
// иконку сменяет текстовая подпись (иконка схлопывается, подпись выезжает).
function PhasePill({
  tone,
  icon,
  label,
  body,
  title,
}: {
  tone: Tone;
  icon: ReactNode;
  label: string;
  body: ReactNode;
  title: string;
}) {
  return (
    <Pill
      tone={tone}
      title={title}
      aria-label={title}
      className="group rounded tabular-nums transition-all duration-200"
    >
      {/* иконка ↔ подпись в одном слоте: иконка схлопывается, подпись выезжает */}
      <span className="inline-flex items-center">
        <span className="inline-flex items-center overflow-hidden transition-all duration-200 max-w-[14px] opacity-100 group-hover:max-w-0 group-hover:opacity-0">
          {icon}
        </span>
        <span className="inline-flex items-center overflow-hidden whitespace-nowrap transition-all duration-200 max-w-0 opacity-0 group-hover:max-w-[110px] group-hover:opacity-100">
          {label}
        </span>
      </span>
      {body}
    </Pill>
  );
}

// Три пилюли шапки, по одной на фазу синка, у каждой свой цикл: ожидание (—),
// идёт (X/Y), застряла (⚠ X/Y) или готово (время последнего синка). Опрос
// частый, пока какая-то фаза идёт, редкий в простое. Триггер принудительно
// обновляет этот ключ для мгновенного отклика (см. PhaseSyncButton).
export function PhaseStatusPills() {
  const { data } = useSWR<Status>("/api/sync-status", {
    refreshInterval: (d) =>
      d?.phases && Object.values(d.phases).some((p) => p.state === "running")
        ? 2_000
        : 5_000,
    // Глобальный SWRConfig ставит dedupingInterval: 60s — он схлопнул бы наш
    // частый опрос до одного запроса в минуту. Переопределяем, чтобы статус
    // действительно обновлялся живьём.
    dedupingInterval: 1_000,
    revalidateOnFocus: true,
    revalidateIfStale: true,
  });

  return (
    <div className="hidden sm:flex items-center gap-1.5">
      {PHASES.map(({ key, label, icon: Icon }) => {
        const p = data?.phases?.[key] ?? {
          state: "never" as const,
          done: 0,
          total: 0,
          syncedAt: null,
        };
        const { tone, icon, body, title } = render(label, Icon, p);
        return (
          <PhasePill
            key={key}
            tone={tone}
            icon={icon}
            label={label}
            body={body}
            title={title}
          />
        );
      })}
      {/* 4-я пилюля — последний синк рег (непрерывный cron; только время). */}
      <PhasePill
        tone={data?.regsLastSyncAt ? "ok" : "muted"}
        icon={<ClipboardList className="h-3 w-3" />}
        label="Реги"
        body={data?.regsLastSyncAt ? relativeTime(data.regsLastSyncAt) : "—"}
        title={
          data?.regsLastSyncAt
            ? `Реги: последняя синхронизация ${relativeTime(data.regsLastSyncAt)}`
            : "Реги: синхронизация ещё не выполнялась"
        }
      />
    </div>
  );
}
